import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../utils/axios";
import { API_ROUTES } from "./routes";
import { showToast } from "components/ui/toast";

// ─── Hooks ───────────────────────────────────────────────────────────────────

export const useGetRegistrySearchUsageQuery = () => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_REGISTRY_SEARCH_USAGE],
    queryFn: () => api.get(API_ROUTES.GET_REGISTRY_SEARCH_USAGE),
  });
  return {
    summary: data?.data?.data?.summary ?? null,
    companies: data?.data?.data?.companies ?? [],
    isPending,
    isError,
    error,
  };
};

export const useGetRegistrySearchUsageByOrgQuery = (organizationId) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_REGISTRY_SEARCH_USAGE_BY_ORG, organizationId],
    queryFn: () =>
      api.get(API_ROUTES.GET_REGISTRY_SEARCH_USAGE_BY_ORG.replace(":organizationId", organizationId)),
    enabled: !!organizationId,
  });
  return {
    summary: data?.data?.data?.summary ?? null,
    entries: data?.data?.data?.entries ?? [],
    isPending,
    isError,
    error,
  };
};

export const useUpdateRegistrySearchUsageMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isPending, isError, error } = useMutation({
    mutationFn: ({ id, organizationId, ...body }) =>
      api.put(API_ROUTES.UPDATE_REGISTRY_SEARCH_USAGE.replace(":id", id), body),
    onSuccess: () => showToast("success", "Record updated"),
    onError: (err) =>
      showToast("error", err?.response?.data?.message || "Update failed"),
    onSettled: (_, __, { organizationId }) => {
      queryClient.invalidateQueries([API_ROUTES.GET_REGISTRY_SEARCH_USAGE]);
      if (organizationId) {
        queryClient.invalidateQueries([API_ROUTES.GET_REGISTRY_SEARCH_USAGE_BY_ORG, organizationId]);
      }
    },
  });
  return { mutate, isPending, isError, error };
};

export const useGetRegistrySearchBreakdownQuery = (params = {}, enabled = true) => {
  const { data, isPending, isError } = useQuery({
    queryKey: [API_ROUTES.GET_REGISTRY_SEARCH_BREAKDOWN, params],
    queryFn: () => api.get(API_ROUTES.GET_REGISTRY_SEARCH_BREAKDOWN, { params }),
    enabled,
  });
  return {
    data: data?.data?.data ?? null,
    isPending,
    isError,
  };
};

// Marks every unpaid entry for an org in a given month ("YYYY-MM") as paid in one request.
export const useMarkRegistrySearchMonthPaidMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isPending, isError, error } = useMutation({
    mutationFn: ({ organizationId, month }) =>
      api.put(API_ROUTES.MARK_REGISTRY_SEARCH_MONTH_PAID, { organizationId, month }),
    onSuccess: () => showToast("success", "Month marked as paid"),
    onError: (err) =>
      showToast("error", err?.response?.data?.message || "Failed to mark month as paid"),
    onSettled: (_, __, { organizationId }) => {
      queryClient.invalidateQueries([API_ROUTES.GET_REGISTRY_SEARCH_USAGE]);
      queryClient.invalidateQueries([API_ROUTES.GET_REGISTRY_SEARCH_USAGE_BY_ORG, organizationId]);
    },
  });
  return { mutate, isPending, isError, error };
};
